import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useVendor } from '@/contexts/VendorContext';
import type { PendingVendorInvitation } from '@/lib/vendorTeamInvitations';

interface PendingInvitationBannerViewProps {
  invitations: PendingVendorInvitation[];
  acceptingProfileId: string | null;
  onAccept: (invitation: PendingVendorInvitation) => void;
  /** True when the last pending-invitation load failed (not "no invitations"). */
  loadError?: boolean;
  onRetry?: () => void;
}

/**
 * Presentational banner — pure props, no context. Rendered by the container below
 * and exercised directly in Storybook / tests for the loading, single- and
 * multi-invitation, and load-error/retry variants.
 */
export function PendingInvitationBannerView({
  invitations,
  acceptingProfileId,
  onAccept,
  loadError = false,
  onRetry,
}: PendingInvitationBannerViewProps) {
  if ((!invitations || invitations.length === 0) && !loadError) {
    return null;
  }

  if ((!invitations || invitations.length === 0) && loadError) {
    return (
      <Alert className="mb-6 border-amber-200 bg-amber-50/70 dark:border-amber-900/60 dark:bg-amber-950/30">
        <Mail className="h-4 w-4 text-amber-600" />
        <AlertTitle>Couldn't load your invitations</AlertTitle>
        <AlertDescription className="space-y-3">
          <p className="text-sm">We couldn't check for pending invitations just now.</p>
          <Button size="sm" variant="outline" onClick={() => onRetry?.()}>Try again</Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="mb-6 border-amber-200 bg-amber-50/70 dark:border-amber-900/60 dark:bg-amber-950/30">
      <Mail className="h-4 w-4 text-amber-600" />
      <AlertTitle>You have {invitations.length === 1 ? 'a pending invitation' : 'pending invitations'}</AlertTitle>
      <AlertDescription className="space-y-3">
        <p className="text-sm">Accept to gain access to the supplier profile you were invited to.</p>
        <div className="space-y-2">
          {invitations.map((invitation) => (
            <div
              key={invitation.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-200 bg-white/60 p-3 dark:border-amber-900/60 dark:bg-slate-900/40"
            >
              <div className="min-w-0">
                <p className="truncate font-medium">
                  {invitation.companyName || invitation.domain || 'Supplier profile'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Role: {invitation.role === 'admin' ? 'Admin' : invitation.role === 'owner' ? 'Owner' : 'Member'}
                </p>
              </div>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => onAccept(invitation)}
                disabled={acceptingProfileId !== null}
              >
                {acceptingProfileId === invitation.vendorProfileId ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-1" /> Accepting…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Accept
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      </AlertDescription>
    </Alert>
  );
}

/**
 * ENT-3797: in-app surface for a signed-in user's pending vendor team
 * invitations. Renders inside the vendor shell so a user who already has one
 * supplier membership discovers (and can accept) invitations to other supplier
 * profiles without needing the email deep-link. Acceptance goes through the
 * backend RPC via VendorContext.acceptInvitation.
 */
export function PendingInvitationBanner() {
  const {
    pendingInvitations,
    pendingInvitationsError,
    refreshPendingInvitations,
    acceptInvitation,
  } = useVendor();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [acceptingProfileId, setAcceptingProfileId] = useState<string | null>(null);

  const handleAccept = async (invitation: PendingVendorInvitation) => {
    setAcceptingProfileId(invitation.vendorProfileId);
    try {
      const { error } = await acceptInvitation(invitation.vendorProfileId);
      if (error) {
        toast({ title: 'Could not accept invitation', description: error, variant: 'destructive' });
        return;
      }
      toast({
        title: 'Invitation accepted',
        description: `You now have access to ${invitation.companyName || 'the supplier profile'}.`,
      });
      // Select the supplier profile that was just accepted. Without steering the
      // workspace, the context keeps preferring the previously-active supplier and
      // the newly granted one is unreachable. Deep-linking by its domain makes the
      // vendor bootstrap prefer that membership.
      navigate(invitation.domain ? `/vendor?domain=${encodeURIComponent(invitation.domain)}` : '/vendor');
    } finally {
      setAcceptingProfileId(null);
    }
  };

  return (
    <PendingInvitationBannerView
      invitations={pendingInvitations}
      acceptingProfileId={acceptingProfileId}
      onAccept={handleAccept}
      loadError={pendingInvitationsError}
      onRetry={() => {
        void refreshPendingInvitations();
      }}
    />
  );
}
